import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { normalizeError } from "../../lib/normalizeError";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Separator } from "../../components/ui/separator";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { 
  Cloud, 
  CheckCircle, 
  XCircle, 
  Upload, 
  RefreshCw, 
  AlertTriangle,
  FolderPlus,
  Mail,
  HardDrive,
  FileSpreadsheet,
  Download,
  Power,
  PowerOff,
  Info,
  Activity,
  Clock,
  SkipForward,
  Key,
  Link,
  Database,
  Shield
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const STATUS_COLORS = {
  NOT_CONFIGURED: "bg-muted text-muted-foreground border-border",
  CONFIGURED: "bg-blue-900/30 text-blue-400 border-blue-900",
  ENABLED: "bg-green-900/30 text-green-400 border-green-900",
  DISABLED: "bg-yellow-900/30 text-yellow-400 border-yellow-900"
};

export default function AdminGoogleWizard() {
  const { getAuthHeader } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState({ drive: false, gmail: false, sheets: false, folders: false, export: false, sync: false, diagnostic: false, events: false, resolve: false });
  const [saving, setSaving] = useState({ drive: false, gmail: false, sheets: false, oauth: false });
  const [testEmail, setTestEmail] = useState("");
  const [diagnosticResult, setDiagnosticResult] = useState(null);
  const [oauthStatus, setOauthStatus] = useState(null);
  const [oauthEnabled, setOauthEnabled] = useState(false);
  
  // First-party OAuth settings state
  const [oauthSettings, setOauthSettings] = useState({
    google_oauth_client_id: "",
    google_oauth_client_secret: "",
    google_oauth_redirect_uri: "",
    google_allowed_domains: ""
  });
  const [oauthConfigured, setOauthConfigured] = useState(false);
  
  const [formData, setFormData] = useState({
    root_id: "",
    sheets_inventory_spreadsheet_id: "",
    sheets_tab_name: "Inventory",
    delegated_sender_email: "",
    default_sender_name: "Kanban Inventory",
    google_allowed_domains: ""
  });

  useEffect(() => {
    fetchSettings();
    fetchOAuthSettings();
  }, []);

  const fetchOAuthSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/google/oauth-settings`, { withCredentials: true });
      const domains = res.data.google_allowed_domains || [];
      setOauthSettings({
        google_oauth_client_id: res.data.google_oauth_client_id || "",
        google_oauth_client_secret: "", // Never returned from server
        google_oauth_redirect_uri: res.data.google_oauth_redirect_uri || "",
        google_allowed_domains: Array.isArray(domains) ? domains.join(", ") : ""
      });
      setOauthConfigured(res.data.oauth_configured);
    } catch (error) {
      console.log("Failed to fetch OAuth settings");
    }
  };

  const fetchSettings = async () => {
    try {
      const [settingsRes, oauthRes] = await Promise.all([
        axios.get(`${API}/admin/google-settings`, { withCredentials: true }),
        axios.get(`${API}/admin/google/oauth-status`, { withCredentials: true }).catch(() => ({ data: { configured: false, enabled: false } }))
      ]);
      setSettings(settingsRes.data);
      setOauthStatus(oauthRes.data);
      setOauthEnabled(settingsRes.data.google_oauth_enabled || false);
      
      // Convert domains array to comma-separated string for input
      const domains = settingsRes.data.google_allowed_domains || [];
      const domainsStr = Array.isArray(domains) ? domains.join(", ") : "";
      
      setFormData({
        root_id: settingsRes.data.root_id || settingsRes.data.drive_root_folder_id || settingsRes.data.drive_shared_drive_id || "",
        sheets_inventory_spreadsheet_id: settingsRes.data.sheets_inventory_spreadsheet_id || "",
        sheets_tab_name: settingsRes.data.sheets_tab_name || "Inventory",
        delegated_sender_email: settingsRes.data.delegated_sender_email || "",
        default_sender_name: settingsRes.data.default_sender_name || "Kanban Inventory",
        google_allowed_domains: domainsStr
      });
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      // Convert comma-separated domains to array
      const domainsArray = formData.google_allowed_domains
        ? formData.google_allowed_domains.split(',').map(d => d.trim()).filter(d => d)
        : [];
      
      await axios.put(`${API}/admin/google-settings`, {
        root_id: formData.root_id || null,
        sheets_inventory_spreadsheet_id: formData.sheets_inventory_spreadsheet_id || null,
        sheets_tab_name: formData.sheets_tab_name || null,
        delegated_sender_email: formData.delegated_sender_email || null,
        default_sender_name: formData.default_sender_name || null,
        google_allowed_domains: domainsArray.length > 0 ? domainsArray : null
      }, { withCredentials: true });
      toast.success("Settings saved");
      fetchSettings();
    } catch (error) {
      toast.error("Failed to save settings");
    }
  };

  // Per-section save handlers
  const handleSaveDrive = async () => {
    setSaving(prev => ({ ...prev, drive: true }));
    try {
      await axios.put(`${API}/admin/google-settings`, {
        root_id: formData.root_id || null
      }, { withCredentials: true });
      toast.success("Drive settings saved");
      fetchSettings();
    } catch (error) {
      toast.error("Failed to save Drive settings");
    } finally {
      setSaving(prev => ({ ...prev, drive: false }));
    }
  };

  const handleSaveGmail = async () => {
    setSaving(prev => ({ ...prev, gmail: true }));
    try {
      await axios.put(`${API}/admin/google-settings`, {
        delegated_sender_email: formData.delegated_sender_email || null,
        default_sender_name: formData.default_sender_name || null
      }, { withCredentials: true });
      toast.success("Gmail settings saved");
      fetchSettings();
    } catch (error) {
      toast.error("Failed to save Gmail settings");
    } finally {
      setSaving(prev => ({ ...prev, gmail: false }));
    }
  };

  const handleSaveSheets = async () => {
    setSaving(prev => ({ ...prev, sheets: true }));
    try {
      await axios.put(`${API}/admin/google-settings`, {
        sheets_inventory_spreadsheet_id: formData.sheets_inventory_spreadsheet_id || null,
        sheets_tab_name: formData.sheets_tab_name || null
      }, { withCredentials: true });
      toast.success("Sheets settings saved");
      fetchSettings();
    } catch (error) {
      toast.error("Failed to save Sheets settings");
    } finally {
      setSaving(prev => ({ ...prev, sheets: false }));
    }
  };

  const handleToggleOAuth = async (enabled) => {
    try {
      await axios.post(`${API}/admin/google/oauth-toggle`, {
        enabled
      }, { withCredentials: true });
      setOauthEnabled(enabled);
      toast.success(enabled ? "OAuth enabled" : "OAuth disabled");
      fetchSettings();
    } catch (error) {
      toast.error("Failed to toggle OAuth");
    }
  };

  const handleSaveOAuth = async () => {
    setSaving(prev => ({ ...prev, oauth: true }));
    try {
      // Convert comma-separated domains to array
      const domainsArray = oauthSettings.google_allowed_domains
        ? oauthSettings.google_allowed_domains.split(',').map(d => d.trim()).filter(d => d)
        : [];
      
      const payload = {
        google_oauth_client_id: oauthSettings.google_oauth_client_id || null,
        google_oauth_redirect_uri: oauthSettings.google_oauth_redirect_uri || null,
        google_allowed_domains: domainsArray.length > 0 ? domainsArray : []
      };
      
      // Only include secret if it was changed (not empty)
      if (oauthSettings.google_oauth_client_secret) {
        payload.google_oauth_client_secret = oauthSettings.google_oauth_client_secret;
      }
      
      await axios.post(`${API}/admin/google/oauth-settings`, payload, { withCredentials: true });
      toast.success("OAuth settings saved");
      fetchOAuthSettings();
      // Clear the secret field after save
      setOauthSettings(prev => ({ ...prev, google_oauth_client_secret: "" }));
    } catch (error) {
      toast.error("Failed to save OAuth settings");
    } finally {
      setSaving(prev => ({ ...prev, oauth: false }));
    }
  };

  const handleUploadServiceAccount = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    
    try {
      const res = await axios.post(`${API}/admin/google-settings/upload-service-account`, formDataUpload, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`Service account uploaded and enabled: ${res.data.email}`);
      fetchSettings();
    } catch (error) {
      toast.error(normalizeError(error.response?.data) || "Failed to upload service account");
    }
  };

  const handleUploadOAuthJson = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    
    try {
      const res = await axios.post(`${API}/admin/google/upload-oauth-json`, formDataUpload, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`OAuth credentials uploaded: ${res.data.client_id}`);
      fetchSettings();
    } catch (error) {
      toast.error(normalizeError(error.response?.data) || "Failed to upload OAuth JSON");
    }
  };

  const handleResolveRootContext = async () => {
    const rootId = formData.root_id;
    if (!rootId) {
      toast.error("Enter a Drive URL or ID first");
      return;
    }
    
    setTesting(prev => ({ ...prev, resolve: true }));
    try {
      const res = await axios.post(`${API}/admin/google/resolve-root-context?root_id=${encodeURIComponent(rootId)}`, {}, { withCredentials: true });
      if (res.data.success) {
        const rootType = res.data.root_type;
        const isDrive = rootType === "DRIVE";
        const isSharedDrive = res.data.is_shared_drive;
        
        if (isDrive) {
          toast.success(`Shared Drive confirmed: "${res.data.name}"`);
        } else if (isSharedDrive) {
          toast.success(`Folder "${res.data.name}" in Shared Drive (driveId: ${res.data.resolved_drive_id})`);
        } else {
          toast.warning(`Folder "${res.data.name}" is in My Drive (not a Shared Drive)`);
        }
        fetchSettings();
      } else {
        toast.error(res.data.error || "Failed to resolve root context");
      }
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to resolve");
    } finally {
      setTesting(prev => ({ ...prev, resolve: false }));
    }
  };

  // Legacy handler - redirect to new one
  const handleResolveDriveContext = handleResolveRootContext;

  const handleToggleEnabled = async () => {
    if (!settings?.service_account_configured) {
      toast.error("Upload service account JSON first");
      return;
    }
    
    const endpoint = settings?.google_enabled ? 
      `${API}/admin/google-settings/disable` : 
      `${API}/admin/google-settings/enable`;
    
    try {
      const res = await axios.post(endpoint, {}, { withCredentials: true });
      toast.success(res.data.message);
      fetchSettings();
    } catch (error) {
      toast.error(normalizeError(error.response?.data) || "Failed to toggle integration");
    }
  };

  const handleTestDrive = async () => {
    setTesting(prev => ({ ...prev, drive: true }));
    try {
      const res = await axios.post(`${API}/admin/google-settings/test-drive`, {}, { withCredentials: true });
      if (res.data.success) {
        toast.success("Drive connection successful!");
      } else {
        toast.error(res.data.error || "Drive test failed");
      }
      fetchSettings();
    } catch (error) {
      toast.error("Drive test failed");
    } finally {
      setTesting(prev => ({ ...prev, drive: false }));
    }
  };

  const handleTestGmail = async () => {
    if (!testEmail) {
      toast.error("Enter a test recipient email");
      return;
    }
    setTesting(prev => ({ ...prev, gmail: true }));
    try {
      const res = await axios.post(`${API}/admin/google-settings/test-gmail?test_recipient=${encodeURIComponent(testEmail)}`, {}, { withCredentials: true });
      if (res.data.success) {
        toast.success("Test email sent successfully!");
      } else {
        toast.error(res.data.error || "Gmail test failed");
      }
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.error || "Gmail test failed");
    } finally {
      setTesting(prev => ({ ...prev, gmail: false }));
    }
  };

  const handleTestSheets = async () => {
    setTesting(prev => ({ ...prev, sheets: true }));
    try {
      const res = await axios.post(`${API}/admin/google-settings/test-sheets`, {}, { withCredentials: true });
      if (res.data.success) {
        toast.success(`Sheets access verified: ${res.data.spreadsheet_title}`);
      } else {
        toast.error(res.data.error || "Sheets test failed");
      }
      fetchSettings();
    } catch (error) {
      toast.error("Sheets test failed");
    } finally {
      setTesting(prev => ({ ...prev, sheets: false }));
    }
  };

  const handleCreateFolders = async () => {
    setTesting(prev => ({ ...prev, folders: true }));
    try {
      const res = await axios.post(`${API}/admin/google-settings/create-folders`, {}, { withCredentials: true });
      if (res.data.success) {
        toast.success("Folder structure created/verified!");
      } else {
        toast.error(res.data.error || "Failed to create folders");
      }
      fetchSettings();
    } catch (error) {
      toast.error("Failed to create folders");
    } finally {
      setTesting(prev => ({ ...prev, folders: false }));
    }
  };

  const handleExportCSV = async () => {
    setTesting(prev => ({ ...prev, export: true }));
    try {
      const res = await axios.post(`${API}/admin/export-csv-to-drive`, {}, { 
        withCredentials: true
      });
      
      // Backend returns JSON with ok:true/false or success:true/false
      if (res.data.ok === true || res.data.success === true) {
        // Success - show link
        const fileName = res.data.file_name || 'inventory-export.csv';
        const driveLink = res.data.drive_link;
        const rows = res.data.rows_exported || 0;
        
        toast.success(
          <div>
            <p>✅ Exported {rows} rows to Drive: {fileName}</p>
            {driveLink && (
              <a href={driveLink} target="_blank" rel="noopener noreferrer" className="underline">
                View in Drive →
              </a>
            )}
          </div>,
          { duration: 6000 }
        );
      } else {
        // Failure - show error
        const errorMsg = res.data.message || res.data.error || 'Export failed';
        toast.error(`Export failed: ${errorMsg}`);
      }
    } catch (error) {
      const errMsg = error.response?.data?.message || error.response?.data?.error || error.message || 'Unknown error';
      toast.error(`Export failed: ${errMsg}`);
    } finally {
      setTesting(prev => ({ ...prev, export: false }));
    }
  };

  const handleExportEvents = async () => {
    setTesting(prev => ({ ...prev, events: true }));
    try {
      const res = await axios.post(`${API}/admin/events/export-to-drive`, {}, { 
        withCredentials: true
      });
      
      // Backend returns JSON with ok:true/false or success:true/false
      if (res.data.ok === true || res.data.success === true) {
        // Success - show link
        const fileName = res.data.file_name || 'events-export.csv';
        const driveLink = res.data.drive_link;
        const count = res.data.exported_count || 0;
        
        toast.success(
          <div>
            <p>✅ Exported {count} events to Drive: {fileName}</p>
            {driveLink && (
              <a href={driveLink} target="_blank" rel="noopener noreferrer" className="underline">
                View in Drive →
              </a>
            )}
          </div>,
          { duration: 6000 }
        );
      } else {
        // Failure - show error
        const errorMsg = res.data.message || res.data.error || 'Export failed';
        toast.error(`Export failed: ${errorMsg}`);
      }
    } catch (error) {
      const errMsg = error.response?.data?.message || error.response?.data?.error || error.message || 'Unknown error';
      toast.error(`Export failed: ${errMsg}`);
    } finally {
      setTesting(prev => ({ ...prev, events: false }));
    }
  };

  const handleSyncSheets = async () => {
    setTesting(prev => ({ ...prev, sync: true }));
    try {
      const res = await axios.post(`${API}/admin/sync-to-sheets`, {}, { withCredentials: true });
      
      // Check both ok and success for backward compatibility
      if (res.data.ok === true || res.data.success === true) {
        const rows = res.data.rows_written || 0;
        const tabs = res.data.tabs || {};
        const tabsCreated = Object.entries(tabs).filter(([, status]) => status === 'created').length;
        const tabsExist = Object.entries(tabs).filter(([, status]) => status === 'exists').length;
        
        let message = `Synced ${rows} rows to Google Sheets`;
        if (tabsCreated > 0) {
          message += ` (${tabsCreated} new tabs created, ${tabsExist} existing)`;
        }
        
        toast.success(message, { duration: 5000 });
      } else {
        const errorMsg = res.data.message || res.data.error || "Sync failed";
        toast.error(`Sync failed: ${errorMsg}`);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.error || "Sync failed";
      toast.error(`Sync failed: ${errorMsg}`);
    } finally {
      setTesting(prev => ({ ...prev, sync: false }));
    }
  };

  const handleImportFromSheet = async () => {
    setTesting(prev => ({ ...prev, import: true }));
    try {
      const res = await axios.post(`${API}/admin/import-from-sheet`, {});
      if (res.data.ok) {
        const counts = res.data.imported || {};
        const parts = Object.entries(counts).filter(([k]) => !k.includes("error")).map(([k, v]) => `${k}: ${v}`);
        toast.success(`Imported from Sheet: ${parts.join(", ")} (${res.data.elapsed_seconds}s)`, { duration: 8000 });
      } else {
        toast.error("Import failed");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.detail || "Import failed");
    } finally {
      setTesting(prev => ({ ...prev, import: false }));
    }
  };


  const handleDownloadCSV = async () => {
    try {
      const token = localStorage.getItem('kanban_token');
      const response = await fetch(`${API}/admin/export-csv-download`, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to download CSV' }));
        toast.error(error.detail || 'Failed to download CSV');
        return;
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      // Create download link
      const a = document.createElement('a');
      a.href = url;
      a.download = 'inventory-export.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      // Clean up blob URL after download
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error('CSV download error:', error);
      toast.error('Failed to download CSV');
    }
  };

  const handleRunDiagnostic = async () => {
    setTesting(prev => ({ ...prev, diagnostic: true }));
    setDiagnosticResult(null);
    try {
      // Include test email if configured
      const params = testEmail ? `?test_email_recipient=${encodeURIComponent(testEmail)}` : '';
      const res = await axios.post(`${API}/admin/google/run-diagnostic${params}`, {}, { withCredentials: true });
      setDiagnosticResult(res.data);
      if (res.data.overall_success) {
        toast.success("Diagnostic passed!");
      } else {
        toast.warning("Diagnostic completed with issues");
      }
      fetchSettings();
    } catch (error) {
      toast.error(normalizeError(error.response?.data) || "Diagnostic failed");
      setDiagnosticResult({ error: normalizeError(error.response?.data) || "Failed to run diagnostic" });
    } finally {
      setTesting(prev => ({ ...prev, diagnostic: false }));
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return null;
    return new Date(ts).toLocaleString();
  };

  const getCheckIcon = (check) => {
    if (!check) return <XCircle className="w-4 h-4 text-muted-foreground" />;
    if (check.status === "SKIPPED") return <SkipForward className="w-4 h-4 text-yellow-400" />;
    if (check.success) return <CheckCircle className="w-4 h-4 text-green-400" />;
    return <XCircle className="w-4 h-4 text-red-400" />;
  };

  const getCheckStatus = (check) => {
    if (!check) return "N/A";
    if (check.status === "SKIPPED") return "SKIPPED";
    return check.success ? "PASS" : "FAIL";
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  const status = settings?.status || "NOT_CONFIGURED";

  return (
    <div className="flex-1 flex flex-col">
      {/* Header */}
      <div className="h-16 border-b border-border px-6 flex items-center justify-between bg-card">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 border border-primary/30 rounded-sm flex items-center justify-center">
            <Cloud className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="font-heading text-2xl tracking-tight">GOOGLE WORKSPACE</h1>
            <p className="text-xs text-muted-foreground">Configure Drive, Gmail, and Sheets integration</p>
          </div>
        </div>
        
        <Badge className={`industrial-badge text-sm ${STATUS_COLORS[status]}`} data-testid="google-status-badge">
          {status.replace('_', ' ')}
        </Badge>
      </div>
      
      <ScrollArea className="flex-1">
        <div className="p-6 max-w-4xl mx-auto space-y-6">
          {/* Master Status & Control */}
          <Card className="industrial-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg">INTEGRATION STATUS</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-5 gap-4">
                <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-sm">
                  {settings?.service_account_configured ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-muted-foreground" />
                  )}
                  <div>
                    <p className="text-sm font-medium">Service Account</p>
                    <p className="text-xs text-muted-foreground truncate max-w-[100px]">
                      {settings?.service_account_email?.split('@')[0] || "Not set"}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-sm">
                  {settings?.delegated_sender_email ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-muted-foreground" />
                  )}
                  <div>
                    <p className="text-sm font-medium">Gmail DWD</p>
                    <p className="text-xs text-muted-foreground truncate max-w-[100px]">
                      {settings?.delegated_sender_email?.split('@')[0] || "Not set"}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-sm">
                  {settings?.drive_folders_created ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-muted-foreground" />
                  )}
                  <div>
                    <p className="text-sm font-medium">Drive Folders</p>
                    <p className="text-xs text-muted-foreground">
                      {settings?.drive_folders_created ? "Created" : "Not created"}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-sm">
                  {settings?.resolved_drive_id || settings?.drive_shared_drive_id ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-muted-foreground" />
                  )}
                  <div>
                    <p className="text-sm font-medium">Shared Drive</p>
                    <p className="text-xs text-muted-foreground truncate max-w-[100px]">
                      {settings?.resolved_drive_id ? "Resolved" : settings?.drive_shared_drive_id ? "Configured" : "Not set"}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-sm">
                  {settings?.google_enabled ? (
                    <Power className="w-5 h-5 text-green-500" />
                  ) : (
                    <PowerOff className="w-5 h-5 text-muted-foreground" />
                  )}
                  <div>
                    <p className="text-sm font-medium">Integration</p>
                    <p className="text-xs text-muted-foreground">
                      {settings?.google_enabled ? "Active" : "Mock Mode"}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Shared Drive Context Display */}
              {(settings?.resolved_drive_id || settings?.drive_shared_drive_id) && (
                <div className="mt-4 p-3 bg-muted/30 rounded-sm space-y-2">
                  <p className="text-xs text-muted-foreground uppercase font-medium flex items-center gap-2">
                    <Database className="w-3 h-3" />
                    Drive Context
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-muted-foreground">Shared Drive ID:</span>
                      <span className="ml-2 font-mono text-green-400">{settings?.drive_shared_drive_id || "Not set"}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Root Folder ID:</span>
                      <span className="ml-2 font-mono text-green-400">{settings?.drive_root_folder_id || "Not set"}</span>
                    </div>
                    {settings?.resolved_drive_id && (
                      <div className="col-span-2">
                        <span className="text-muted-foreground">Resolved driveId:</span>
                        <span className="ml-2 font-mono text-primary">{settings?.resolved_drive_id}</span>
                        <Badge className="ml-2 bg-green-900/30 text-green-400 text-[10px]">SHARED DRIVE</Badge>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Enable/Disable Toggle */}
              {settings?.service_account_configured && (
                <div className="mt-4 p-4 bg-muted/30 rounded-sm flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Google Integration</p>
                    <p className="text-xs text-muted-foreground">
                      {settings?.google_enabled 
                        ? "Real emails and uploads are active" 
                        : "Mock mode - operations logged but not sent"}
                    </p>
                  </div>
                  <Button
                    onClick={handleToggleEnabled}
                    variant={settings?.google_enabled ? "destructive" : "default"}
                    className="uppercase tracking-wider rounded-sm"
                    data-testid="toggle-google-btn"
                  >
                    {settings?.google_enabled ? (
                      <>
                        <PowerOff className="w-4 h-4 mr-2" />
                        Disable
                      </>
                    ) : (
                      <>
                        <Power className="w-4 h-4 mr-2" />
                        Enable
                      </>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Full Diagnostic */}
          <Card className="industrial-card border-primary/30">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-primary" />
                RUN FULL DIAGNOSTIC
              </CardTitle>
              <CardDescription>
                Comprehensive test of all Google integrations (Drive, Gmail, Sheets)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <Button
                  onClick={handleRunDiagnostic}
                  disabled={testing.diagnostic}
                  className="uppercase tracking-wider rounded-sm"
                  data-testid="run-diagnostic-btn"
                >
                  <Activity className={`w-4 h-4 mr-2 ${testing.diagnostic ? "animate-spin" : ""}`} />
                  {testing.diagnostic ? "Running..." : "Run Full Diagnostic"}
                </Button>
                
                {settings?.last_diagnostic && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    Last run: {formatTimestamp(settings.last_diagnostic.timestamp)}
                    <span className={settings.last_diagnostic.overall_success ? "text-green-400" : "text-red-400"}>
                      ({settings.last_diagnostic.overall_success ? "PASSED" : "FAILED"})
                    </span>
                  </div>
                )}
              </div>
              
              {/* Diagnostic Results Panel */}
              {diagnosticResult && (
                <div className={`p-4 rounded-sm border ${diagnosticResult.overall_success ? "bg-green-900/10 border-green-900/30" : diagnosticResult.error ? "bg-red-900/10 border-red-900/30" : "bg-yellow-900/10 border-yellow-900/30"}`}>
                  {diagnosticResult.error ? (
                    <div className="flex items-center gap-2 text-red-400">
                      <XCircle className="w-5 h-5" />
                      <span>{diagnosticResult.error}</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {diagnosticResult.overall_success ? (
                            <CheckCircle className="w-5 h-5 text-green-400" />
                          ) : (
                            <AlertTriangle className="w-5 h-5 text-yellow-400" />
                          )}
                          <span className={`font-medium ${diagnosticResult.overall_success ? "text-green-400" : "text-yellow-400"}`}>
                            {diagnosticResult.summary || (diagnosticResult.overall_success ? "All checks passed" : "Some checks failed")}
                          </span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          Mode: {diagnosticResult.mode}
                        </span>
                      </div>
                      
                      <div className="space-y-2">
                        {diagnosticResult.checks && Object.entries(diagnosticResult.checks).map(([key, check]) => (
                          <div key={key} className="flex items-center justify-between py-1 border-b border-border/50 last:border-0">
                            <div className="flex items-center gap-2">
                              {getCheckIcon(check)}
                              <span className="text-sm font-mono">
                                {key.replace(/_/g, ' ').toUpperCase()}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                check?.status === "SKIPPED" ? "bg-yellow-900/30 text-yellow-400" :
                                check?.success ? "bg-green-900/30 text-green-400" : 
                                "bg-red-900/30 text-red-400"
                              }`}>
                                {getCheckStatus(check)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Drive Access Details - show root_type, interpretation and resolved drive ID */}
                      {diagnosticResult.checks?.drive_access && (
                        <div className="mt-3 p-3 bg-muted/20 rounded-sm space-y-2">
                          <p className="text-xs text-muted-foreground uppercase font-medium flex items-center gap-2">
                            <HardDrive className="w-3 h-3" />
                            Drive Test Details
                          </p>
                          <div className="text-xs space-y-1">
                            {/* Root ID tested */}
                            {diagnosticResult.checks.drive_access.root_id && (
                              <div>
                                <span className="text-muted-foreground">Root ID:</span>
                                <span className="ml-2 font-mono text-primary">{diagnosticResult.checks.drive_access.root_id}</span>
                              </div>
                            )}
                            {/* Root Type detected */}
                            {diagnosticResult.checks.drive_access.root_type && (
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground">Root Type:</span>
                                <Badge className={`ml-1 ${
                                  diagnosticResult.checks.drive_access.root_type === 'DRIVE' 
                                    ? 'bg-blue-900/30 text-blue-400' 
                                    : diagnosticResult.checks.drive_access.root_type === 'FOLDER'
                                    ? 'bg-yellow-900/30 text-yellow-400'
                                    : 'bg-red-900/30 text-red-400'
                                }`}>
                                  {diagnosticResult.checks.drive_access.root_type}
                                </Badge>
                              </div>
                            )}
                            {/* Interpretation message */}
                            {diagnosticResult.checks.drive_access.interpretation && (
                              <div className={`${diagnosticResult.checks.drive_access.success ? "text-green-400" : "text-yellow-400"}`}>
                                {diagnosticResult.checks.drive_access.interpretation}
                              </div>
                            )}
                            {/* Resolved driveId - only show if actually resolved */}
                            {diagnosticResult.checks.drive_access.resolved_drive_id && diagnosticResult.checks.drive_access.success && (
                              <div>
                                <span className="text-muted-foreground">Resolved driveId:</span>
                                <span className="ml-2 font-mono text-green-400">{diagnosticResult.checks.drive_access.resolved_drive_id}</span>
                                <Badge className="ml-2 bg-green-900/30 text-green-400 text-[10px]">SHARED DRIVE</Badge>
                              </div>
                            )}
                            {/* Name of folder/drive */}
                            {diagnosticResult.checks.drive_access.name && (
                              <div>
                                <span className="text-muted-foreground">Name:</span>
                                <span className="ml-2">{diagnosticResult.checks.drive_access.name}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Sheets Access Details */}
                      {diagnosticResult.checks?.sheets_access && !diagnosticResult.checks.sheets_access.success && diagnosticResult.checks.sheets_access.error && (
                        <div className="mt-3 p-3 bg-muted/20 rounded-sm space-y-2">
                          <p className="text-xs text-muted-foreground uppercase font-medium flex items-center gap-2">
                            <FileSpreadsheet className="w-3 h-3" />
                            Sheets Test Details
                          </p>
                          <div className="text-xs space-y-1">
                            {diagnosticResult.checks.sheets_access.tested_spreadsheet_id && (
                              <div>
                                <span className="text-muted-foreground">Tested Sheet ID:</span>
                                <span className="ml-2 font-mono text-primary">{diagnosticResult.checks.sheets_access.tested_spreadsheet_id}</span>
                              </div>
                            )}
                            {diagnosticResult.checks.sheets_access.service_account_email && (
                              <div>
                                <span className="text-muted-foreground">Service Account:</span>
                                <span className="ml-2 font-mono">{diagnosticResult.checks.sheets_access.service_account_email}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Show errors and remediation for failed checks */}
                      {diagnosticResult.checks && Object.entries(diagnosticResult.checks)
                        .filter(([_, check]) => check?.error || check?.reason || check?.remediation)
                        .map(([key, check]) => (
                          <div key={`${key}-error`} className="mt-2 p-3 bg-red-900/10 border border-red-900/30 rounded-sm">
                            <div className="flex items-center gap-2 mb-2">
                              <XCircle className="w-4 h-4 text-red-400" />
                              <span className="font-mono text-sm text-red-400">{key.replace(/_/g, ' ').toUpperCase()}</span>
                            </div>
                            {(check.error || check.reason) && (
                              <p className="text-xs text-red-300 font-mono break-all mb-2">{check.error || check.reason}</p>
                            )}
                            {check.remediation && (
                              <div className="mt-2 p-2 bg-yellow-900/20 border border-yellow-900/30 rounded">
                                <p className="text-xs text-yellow-400 font-medium mb-1">How to Fix:</p>
                                <pre className="text-xs text-yellow-300 whitespace-pre-wrap font-mono">{check.remediation}</pre>
                              </div>
                            )}
                          </div>
                        ))
                      }
                      
                      <div className="mt-3 pt-3 border-t border-border/50 text-xs text-muted-foreground flex items-center gap-2">
                        <Clock className="w-3 h-3" />
                        {formatTimestamp(diagnosticResult.timestamp)}
                        {diagnosticResult.run_by && <span>• Run by: {diagnosticResult.run_by}</span>}
                      </div>
                    </>
                  )}
                </div>
              )}
              
              <div className="p-3 bg-muted/30 rounded-sm">
                <div className="flex items-start gap-2 text-xs text-muted-foreground">
                  <Info className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    <p><strong>ENABLED mode:</strong> Tests real Drive access, Gmail sending (if test email provided), and Sheets connectivity.</p>
                    <p className="mt-1"><strong>DISABLED/NOT_CONFIGURED:</strong> Shows skipped checks and confirms mock mode is active.</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Step 1: Service Account */}
          <Card className="industrial-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <span className="w-6 h-6 rounded-sm bg-primary/20 text-primary text-sm flex items-center justify-center">1</span>
                SERVICE ACCOUNT + DOMAIN-WIDE DELEGATION
              </CardTitle>
              <CardDescription>
                Upload service account JSON for Drive, Gmail, and Sheets access
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {settings?.service_account_configured ? (
                <div className="p-4 bg-green-900/10 border border-green-900/30 rounded-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <div>
                        <p className="text-sm text-green-400 font-medium">Service Account Configured</p>
                        <p className="text-xs font-mono text-muted-foreground mt-1">{settings.service_account_email}</p>
                        {settings.service_account_project_id && (
                          <p className="text-xs text-muted-foreground">Project: {settings.service_account_project_id}</p>
                        )}
                      </div>
                    </div>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".json"
                        onChange={handleUploadServiceAccount}
                        className="hidden"
                      />
                      <Button type="button" variant="ghost" size="sm" className="text-xs" asChild>
                        <span>Replace</span>
                      </Button>
                    </label>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-muted/30 border border-dashed border-border rounded-sm">
                  <div className="text-center">
                    <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground mb-3">Upload service account JSON file</p>
                    <p className="text-xs text-muted-foreground mb-3">
                      Must contain: client_email, private_key, project_id
                    </p>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".json"
                        onChange={handleUploadServiceAccount}
                        className="hidden"
                        data-testid="upload-service-account"
                      />
                      <Button type="button" variant="secondary" className="uppercase tracking-wider rounded-sm" asChild>
                        <span>Select File</span>
                      </Button>
                    </label>
                  </div>
                </div>
              )}
              
              <Separator />
              
              <div className="space-y-3">
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground uppercase flex items-center gap-2">
                    Delegated Sender Email
                    <span className="text-red-400">*</span>
                    <span className="text-yellow-400 text-[10px] normal-case">(Required for Gmail)</span>
                  </label>
                  <Input
                    value={formData.delegated_sender_email}
                    onChange={(e) => setFormData({ ...formData, delegated_sender_email: e.target.value })}
                    placeholder="purchasing@yourdomain.com"
                    className="industrial-input font-mono"
                    data-testid="delegated-sender-email"
                  />
                  <p className="text-xs text-muted-foreground">
                    Email of user to impersonate via Domain-Wide Delegation. Must be in your Workspace domain.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground uppercase">Default Sender Name</label>
                  <Input
                    value={formData.default_sender_name}
                    onChange={(e) => setFormData({ ...formData, default_sender_name: e.target.value })}
                    placeholder="Kanban Inventory"
                    className="industrial-input"
                    data-testid="sender-name"
                  />
                </div>
              </div>
              
              {!settings?.delegated_sender_email && settings?.service_account_configured && (
                <div className="p-3 bg-yellow-900/10 border border-yellow-900/30 rounded-sm">
                  <div className="flex items-start gap-2 text-yellow-400">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>
                      <span className="text-xs font-medium">Delegated sender email required for Gmail</span>
                      <p className="text-xs text-muted-foreground mt-1">
                        Gmail API requires impersonating a real user in your domain. Set the email above and save.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Save Service Account Settings Button */}
              <div className="flex justify-end pt-2 border-t border-border/50">
                <Button
                  onClick={handleSaveGmail}
                  disabled={saving.gmail}
                  className="bg-primary hover:bg-primary/90"
                >
                  {saving.gmail ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>Save Email Settings</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Step 2: Drive Configuration */}
          <Card className="industrial-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <span className="w-6 h-6 rounded-sm bg-primary/20 text-primary text-sm flex items-center justify-center">2</span>
                <HardDrive className="w-5 h-5" />
                DRIVE CONFIGURATION
              </CardTitle>
              <CardDescription>
                Configure Shared Drive for exports and assets
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Single Root ID Input */}
              <div className="space-y-2">
                <label className="text-xs text-muted-foreground uppercase">Root Drive/Folder (ID or URL)</label>
                <Input
                  value={formData.root_id || ""}
                  onChange={(e) => setFormData({ ...formData, root_id: e.target.value })}
                  placeholder="https://drive.google.com/drive/folders/... or ID"
                  className="industrial-input font-mono"
                  data-testid="google-root-id"
                />
                <p className="text-xs text-muted-foreground">
                  Paste a Google Drive folder URL or Shared Drive URL. The system auto-detects whether it&apos;s a Drive or Folder.
                </p>
              </div>
              
              {/* Resolve Root Context Button */}
              <div className="flex gap-3 items-center">
                <Button
                  onClick={handleResolveRootContext}
                  variant="outline"
                  disabled={testing.resolve || !formData.root_id}
                  className="uppercase tracking-wider rounded-sm"
                  data-testid="resolve-root-btn"
                >
                  <Link className={`w-4 h-4 mr-2 ${testing.resolve ? "animate-spin" : ""}`} />
                  {testing.resolve ? "Resolving..." : "Resolve & Verify"}
                </Button>
                <span className="text-xs text-muted-foreground">
                  Verifies the ID and detects root type (Shared Drive or Folder)
                </span>
              </div>
              
              {/* Show resolved context - only when actually resolved from Google API */}
              {settings?.root_context_resolved && (
                <div className="p-3 bg-green-900/20 border border-green-700/50 rounded-sm space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-green-400">Root Context Resolved</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-muted-foreground">Type: </span>
                      <Badge className={`ml-1 ${settings.root_type === 'DRIVE' ? 'bg-blue-900/30 text-blue-400' : 'bg-yellow-900/30 text-yellow-400'}`}>
                        {settings.root_type || 'UNKNOWN'}
                      </Badge>
                    </div>
                    {settings.resolved_drive_id && (
                      <div>
                        <span className="text-muted-foreground">driveId: </span>
                        <span className="font-mono text-green-400">{settings.resolved_drive_id}</span>
                      </div>
                    )}
                    {settings.root_context_resolution?.name && (
                      <div className="col-span-2">
                        <span className="text-muted-foreground">Name: </span>
                        <span className="text-primary">{settings.root_context_resolution.name}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <div className="flex gap-3">
                <Button
                  onClick={handleTestDrive}
                  disabled={testing.drive}
                  variant="secondary"
                  className="uppercase tracking-wider rounded-sm"
                  data-testid="test-drive-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${testing.drive ? "animate-spin" : ""}`} />
                  Test Drive
                </Button>
                
                <Button
                  onClick={handleCreateFolders}
                  disabled={testing.folders}
                  variant="secondary"
                  className="uppercase tracking-wider rounded-sm"
                  data-testid="create-folders-btn"
                >
                  <FolderPlus className={`w-4 h-4 mr-2 ${testing.folders ? "animate-spin" : ""}`} />
                  Create/Verify Folders
                </Button>
              </div>
              
              {/* Drive Test Results */}
              {settings?.last_drive_test && (
                <div className={`p-3 rounded-sm text-sm ${settings.last_drive_test.success ? "bg-green-900/10 border border-green-900/30" : "bg-red-900/10 border border-red-900/30"}`}>
                  <div className="flex justify-between items-start">
                    <span className={settings.last_drive_test.success ? "text-green-400" : "text-red-400"}>
                      Drive Test: {settings.last_drive_test.success ? "SUCCESS" : "FAILED"}
                    </span>
                    <span className="text-xs text-muted-foreground">{formatTimestamp(settings.last_drive_test.timestamp)}</span>
                  </div>
                  {settings.last_drive_test.error && (
                    <p className="text-xs text-red-400 mt-1 font-mono break-all">{settings.last_drive_test.error}</p>
                  )}
                </div>
              )}
              
              {settings?.folder_ids && (
                <div className="p-3 bg-muted/30 rounded-sm">
                  <p className="text-xs text-muted-foreground uppercase mb-2">Created Folders</p>
                  <div className="space-y-1 font-mono text-xs">
                    {Object.entries(settings.folder_ids).slice(0, 4).map(([path, id]) => (
                      <div key={path} className="flex justify-between">
                        <span className="text-muted-foreground truncate max-w-[200px]">{path}</span>
                        <span className="text-green-400">{id.substring(0, 10)}...</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Save Drive Settings Button */}
              <div className="flex justify-end pt-2 border-t border-border/50">
                <Button
                  onClick={handleSaveDrive}
                  disabled={saving.drive}
                  className="bg-primary hover:bg-primary/90"
                >
                  {saving.drive ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>Save Drive Settings</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Step 3: Gmail Test */}
          <Card className="industrial-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <span className="w-6 h-6 rounded-sm bg-primary/20 text-primary text-sm flex items-center justify-center">3</span>
                <Mail className="w-5 h-5" />
                GMAIL TEST
              </CardTitle>
              <CardDescription>
                Send a test email to verify Gmail API access via Domain-Wide Delegation
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-3">
                <Input
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="your-email@example.com"
                  className="industrial-input flex-1"
                  data-testid="test-email-input"
                />
                <Button
                  onClick={handleTestGmail}
                  disabled={testing.gmail}
                  variant="secondary"
                  className="uppercase tracking-wider rounded-sm"
                  data-testid="test-gmail-btn"
                >
                  <Mail className={`w-4 h-4 mr-2 ${testing.gmail ? "animate-spin" : ""}`} />
                  Send Test
                </Button>
              </div>
              
              {/* Gmail Test Results */}
              {settings?.last_gmail_test && (
                <div className={`p-3 rounded-sm text-sm ${settings.last_gmail_test.success ? "bg-green-900/10 border border-green-900/30" : "bg-red-900/10 border border-red-900/30"}`}>
                  <div className="flex justify-between items-start">
                    <span className={settings.last_gmail_test.success ? "text-green-400" : "text-red-400"}>
                      Gmail Test: {settings.last_gmail_test.success ? "SUCCESS" : "FAILED"}
                    </span>
                    <span className="text-xs text-muted-foreground">{formatTimestamp(settings.last_gmail_test.timestamp)}</span>
                  </div>
                  {settings.last_gmail_test.success && settings.last_gmail_test.recipient && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Sent to: {settings.last_gmail_test.recipient} from: {settings.last_gmail_test.sender}
                    </p>
                  )}
                  {settings.last_gmail_test.error && (
                    <p className="text-xs text-red-400 mt-1 font-mono break-all">{settings.last_gmail_test.error}</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Step 4: Sheets Configuration */}
          <Card className="industrial-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <span className="w-6 h-6 rounded-sm bg-primary/20 text-primary text-sm flex items-center justify-center">4</span>
                <FileSpreadsheet className="w-5 h-5" />
                SHEETS CONFIGURATION (OPTIONAL)
              </CardTitle>
              <CardDescription>
                Configure Google Sheets for inventory snapshots
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground uppercase">Spreadsheet ID</label>
                  <Input
                    value={formData.sheets_inventory_spreadsheet_id}
                    onChange={(e) => setFormData({ ...formData, sheets_inventory_spreadsheet_id: e.target.value })}
                    placeholder="1xxxxxxxxxxxxxxxxx"
                    className="industrial-input font-mono"
                    data-testid="google-sheets-id"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground uppercase">Tab Name</label>
                  <Input
                    value={formData.sheets_tab_name}
                    onChange={(e) => setFormData({ ...formData, sheets_tab_name: e.target.value })}
                    placeholder="Inventory"
                    className="industrial-input"
                    data-testid="google-sheets-tab"
                  />
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={handleTestSheets}
                  disabled={testing.sheets}
                  variant="secondary"
                  className="uppercase tracking-wider rounded-sm"
                  data-testid="test-sheets-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${testing.sheets ? "animate-spin" : ""}`} />
                  Test Sheets
                </Button>
                
                <Button
                  onClick={handleSyncSheets}
                  disabled={testing.sync}
                  variant="secondary"
                  className="uppercase tracking-wider rounded-sm"
                  data-testid="sync-sheets-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${testing.sync ? "animate-spin" : ""}`} />
                  Sync to Sheets
                </Button>
                
                <Button
                  onClick={handleImportFromSheet}
                  disabled={testing.import}
                  variant="outline"
                  className="uppercase tracking-wider rounded-sm border-primary/50 text-primary"
                  data-testid="import-from-sheet-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${testing.import ? "animate-spin" : ""}`} />
                  Import from Sheet to Mongo
                </Button>
              </div>
              
              {/* Sheets Test Results */}
              {settings?.last_sheets_test && (
                <div className={`p-3 rounded-sm text-sm ${settings.last_sheets_test.success ? "bg-green-900/10 border border-green-900/30" : "bg-red-900/10 border border-red-900/30"}`}>
                  <div className="flex justify-between items-start">
                    <span className={settings.last_sheets_test.success ? "text-green-400" : "text-red-400"}>
                      Sheets Test: {settings.last_sheets_test.success ? "SUCCESS" : "FAILED"}
                    </span>
                    <span className="text-xs text-muted-foreground">{formatTimestamp(settings.last_sheets_test.timestamp)}</span>
                  </div>
                  {settings.last_sheets_test.success && settings.last_sheets_test.spreadsheet_title && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Spreadsheet: {settings.last_sheets_test.spreadsheet_title}
                    </p>
                  )}
                  {settings.last_sheets_test.error && (
                    <p className="text-xs text-red-400 mt-1 font-mono break-all">{settings.last_sheets_test.error}</p>
                  )}
                </div>
              )}
              
              {/* Save Sheets Settings Button */}
              <div className="flex justify-end pt-2 border-t border-border/50">
                <Button
                  onClick={handleSaveSheets}
                  disabled={saving.sheets}
                  className="bg-primary hover:bg-primary/90"
                >
                  {saving.sheets ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>Save Sheets Settings</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Step 5: Export Operations */}
          <Card className="industrial-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <span className="w-6 h-6 rounded-sm bg-primary/20 text-primary text-sm flex items-center justify-center">5</span>
                <Download className="w-5 h-5" />
                EXPORT OPERATIONS
              </CardTitle>
              <CardDescription>
                Export inventory and events data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground uppercase font-medium">Inventory</p>
                <div className="flex gap-3 flex-wrap">
                  <Button
                    onClick={handleExportCSV}
                    disabled={testing.export}
                    className="uppercase tracking-wider rounded-sm"
                    data-testid="export-csv-drive-btn"
                  >
                    <Cloud className={`w-4 h-4 mr-2 ${testing.export ? "animate-spin" : ""}`} />
                    {status === "ENABLED" && settings?.drive_folders_created ? "Export to Drive" : "Export CSV"}
                  </Button>
                  
                  <Button
                    onClick={handleDownloadCSV}
                    variant="secondary"
                    className="uppercase tracking-wider rounded-sm"
                    data-testid="download-csv-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download CSV
                  </Button>
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground uppercase font-medium">Integration Events (Salesforce-Ready)</p>
                <div className="flex gap-3 flex-wrap">
                  <Button
                    onClick={handleExportEvents}
                    disabled={testing.events}
                    variant="secondary"
                    className="uppercase tracking-wider rounded-sm"
                    data-testid="export-events-btn"
                  >
                    <Cloud className={`w-4 h-4 mr-2 ${testing.events ? "animate-spin" : ""}`} />
                    {status === "ENABLED" && settings?.drive_folders_created ? "Events to Drive" : "Export Events"}
                  </Button>
                </div>
              </div>
              
              <div className="p-3 bg-muted/30 rounded-sm flex items-start gap-2">
                <Info className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                <div className="text-xs text-muted-foreground">
                  {status === "ENABLED" && settings?.drive_folders_created ? (
                    <span>Exports will upload to Google Drive. Files saved to <span className="font-mono">Kanban Inventory/exports/csv</span>.</span>
                  ) : (
                    <span>Google not configured. Exports will download locally instead. Enable Google integration to upload to Drive.</span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Step 6: Google OAuth Login (Collapsible) */}
          <Card className="industrial-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <span className="w-6 h-6 rounded-sm bg-primary/20 text-primary text-sm flex items-center justify-center">6</span>
                    <Key className="w-5 h-5" />
                    GOOGLE OAUTH LOGIN
                  </CardTitle>
                  <CardDescription>
                    Allow users to login with their Google Workspace accounts (allowlist-only)
                  </CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={oauthEnabled ? "bg-green-900/30 text-green-400" : "bg-muted text-muted-foreground"}>
                    {oauthEnabled ? "ENABLED" : "DISABLED"}
                  </Badge>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Enable OAuth Login</span>
                    <Switch
                      checked={oauthEnabled}
                      onCheckedChange={handleToggleOAuth}
                      data-testid="oauth-toggle"
                    />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* When disabled, show collapsed state */}
              {!oauthEnabled && (
                <div className="p-4 bg-muted/30 rounded-sm text-center">
                  <p className="text-sm text-muted-foreground">
                    OAuth login is currently <strong>disabled</strong>. Toggle above to enable and configure.
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    When enabled, users can login with Google accounts (allowlist-only, no auto-registration).
                  </p>
                </div>
              )}
              
              {/* When enabled, show full configuration */}
              {oauthEnabled && (
                <>
                  {/* OAuth Status Banner */}
                  {oauthConfigured ? (
                    <div className="p-3 bg-green-900/10 border border-green-900/30 rounded-sm">
                      <div className="flex items-center gap-2 text-green-400">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">OAuth Configured & Enabled</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Google login button is active. Only users in your SQLite database can login (allowlist enforcement).
                      </p>
                    </div>
                  ) : (
                    <div className="p-3 bg-yellow-900/10 border border-yellow-900/30 rounded-sm">
                      <div className="flex items-center gap-2 text-yellow-400">
                        <AlertTriangle className="w-5 h-5" />
                        <span className="font-medium">OAuth Not Configured</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Configure OAuth settings below and save to enable Google login.
                      </p>
                    </div>
                  )}
                  
                  {/* OAuth Configuration Fields */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-xs text-muted-foreground uppercase">OAuth Client ID</label>
                      <Input
                        value={oauthSettings.google_oauth_client_id}
                        onChange={(e) => setOauthSettings({ ...oauthSettings, google_oauth_client_id: e.target.value })}
                        placeholder="123456789-xxxxx.apps.googleusercontent.com"
                        className="industrial-input font-mono text-sm"
                        data-testid="oauth-client-id"
                      />
                      <p className="text-xs text-muted-foreground">
                        From Google Cloud Console → APIs & Services → Credentials → OAuth 2.0 Client
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-xs text-muted-foreground uppercase">OAuth Client Secret</label>
                      <Input
                        type="password"
                        value={oauthSettings.google_oauth_client_secret}
                        onChange={(e) => setOauthSettings({ ...oauthSettings, google_oauth_client_secret: e.target.value })}
                        placeholder="Leave blank to keep existing"
                        className="industrial-input font-mono text-sm"
                        data-testid="oauth-client-secret"
                      />
                      <p className="text-xs text-muted-foreground">
                        Only enter if setting new secret. Existing secret is not displayed.
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-xs text-muted-foreground uppercase">Redirect URI (Canonical)</label>
                      <Input
                        value={oauthSettings.google_oauth_redirect_uri}
                        onChange={(e) => setOauthSettings({ ...oauthSettings, google_oauth_redirect_uri: e.target.value })}
                        placeholder="https://inventory.vidor.app/api/auth/google/callback"
                        className="industrial-input font-mono text-sm"
                        data-testid="oauth-redirect-uri"
                      />
                      <p className="text-xs text-muted-foreground">
                        Must match <strong>exactly</strong> in Google Console. Recommended: <code className="text-primary">{window.location.origin}/api/auth/google/callback</code>
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-xs text-muted-foreground uppercase">Allowed Email Domains (Defense-in-Depth)</label>
                      <Input
                        value={oauthSettings.google_allowed_domains}
                        onChange={(e) => setOauthSettings({ ...oauthSettings, google_allowed_domains: e.target.value })}
                        placeholder="vidordoor.com, subsidiary.com"
                        className="industrial-input"
                        data-testid="oauth-allowed-domains"
                      />
                      <p className="text-xs text-muted-foreground">
                        Comma-separated. Users from other domains will be blocked even with valid Google login.
                      </p>
                    </div>
                  </div>
                  
                  {/* Setup Instructions */}
                  <div className="p-3 bg-blue-900/10 border border-blue-900/30 rounded-sm">
                    <div className="flex items-start gap-2 text-xs">
                      <Info className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                      <div className="text-blue-300">
                        <p><strong>Setup Steps:</strong></p>
                        <ol className="list-decimal ml-4 mt-1 space-y-1">
                          <li>In Google Cloud Console, create OAuth consent screen (Internal)</li>
                          <li>Create OAuth Client ID (Web application)</li>
                          <li>Add Authorized JavaScript origin: <code>{window.location.origin}</code></li>
                          <li>Add Authorized redirect URI: <code>{window.location.origin}/api/auth/google/callback</code></li>
                          <li>Download client configuration JSON</li>
                          <li>Enter Client ID and Secret above, or use JSON upload</li>
                          <li>Set allowed domains for additional security</li>
                          <li>Click Save OAuth Settings</li>
                        </ol>
                      </div>
                    </div>
                  </div>
                  
                  {/* Allowlist Notice */}
                  <div className="p-3 bg-yellow-900/10 border border-yellow-900/30 rounded-sm">
                    <div className="flex items-start gap-2 text-xs">
                      <Shield className="w-4 h-4 text-yellow-400 mt-0.5 shrink-0" />
                      <div className="text-yellow-300">
                        <p><strong>Allowlist Enforcement:</strong></p>
                        <p className="mt-1">
                          Only users who already exist in the system can login via Google OAuth. 
                          Users must be invited/added by an admin BEFORE they can use Google login.
                          OAuth will NOT auto-create new user accounts.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* OAuth Diagnostics */}
                  <div className="p-3 bg-muted/30 rounded-sm space-y-2">
                    <p className="text-xs text-muted-foreground uppercase font-medium">Current OAuth Configuration</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-muted-foreground">Status:</span>
                        <Badge className={`ml-2 ${oauthConfigured ? "bg-green-900/30 text-green-400" : "bg-yellow-900/30 text-yellow-400"}`}>
                          {oauthConfigured ? "CONFIGURED" : "NOT CONFIGURED"}
                        </Badge>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Login URL:</span>
                        <span className="ml-2 font-mono text-primary">/api/auth/google/start</span>
                      </div>
                      {oauthSettings.google_oauth_client_id && (
                        <div className="col-span-2">
                          <span className="text-muted-foreground">Client ID:</span>
                          <span className="ml-2 font-mono">{oauthSettings.google_oauth_client_id.substring(0, 25)}...</span>
                        </div>
                      )}
                      {oauthSettings.google_oauth_redirect_uri && (
                        <div className="col-span-2">
                          <span className="text-muted-foreground">Redirect:</span>
                          <span className="ml-2 font-mono">{oauthSettings.google_oauth_redirect_uri}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Save OAuth Settings Button */}
                  <div className="flex justify-end pt-2 border-t border-border/50">
                    <Button
                      onClick={handleSaveOAuth}
                      disabled={saving.oauth}
                      className="bg-primary hover:bg-primary/90"
                    >
                      {saving.oauth ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>Save OAuth Settings</>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
          
          {/* Save Button */}
          <div className="flex justify-end gap-3">
            <Button onClick={handleSave} className="uppercase tracking-wider rounded-sm px-8" data-testid="save-google-settings">
              Save Settings
            </Button>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
